/**
 * Reads the images a design-system zip carries, in this browser, so the import preview can show each logo found on the
 * ground it would sit on (ORB-1028).
 *
 * ⚑ The API's preview names the images and their paths but carries no image data: it is stateless, and reads the same zip
 * again when the import is confirmed. The person's own file is already here, so the images are read from it rather than
 * uploaded twice. Nothing read here is sent anywhere.
 *
 * ⚑ Only the paths the API named are read, stored or deflated (the two methods a zip tool writes), and never more than
 * `maxBytes` of any one of them: the zip is a file anyone could hand someone, so its sizes are not trusted.
 */

const END_OF_CENTRAL_DIRECTORY = 0x06054b50
const CENTRAL_DIRECTORY_ENTRY = 0x02014b50
const LOCAL_FILE_HEADER = 0x04034b50
const STORED = 0
const DEFLATED = 8
/** An entry's sizes are this when they live in a ZIP64 record, which a design-system export never needs. */
const ZIP64_SIZE = 0xffffffff

// The API offers a logo up to its DesignSystemImportLimits.MaxImageBytes, so the preview shows every one it offers.
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024

interface Entry {
  method: number
  compressedSize: number
  uncompressedSize: number
  localHeaderOffset: number
}

/** The images at `paths`, by path. A path missing from the zip, too large, or in a form this cannot read is left out. */
export async function readZipImages(
  zip: Blob,
  paths: readonly string[],
  maxBytes = MAX_IMAGE_BYTES,
): Promise<Map<string, Uint8Array>> {
  const bytes = new Uint8Array(await zip.arrayBuffer())
  const entries = centralDirectory(bytes)
  const images = new Map<string, Uint8Array>()
  if (entries === null) return images

  for (const path of paths) {
    const entry = entries.get(path)
    if (!entry || entry.uncompressedSize > maxBytes || entry.compressedSize === ZIP64_SIZE) continue
    const data = entryData(bytes, entry)
    if (data === null) continue
    const content = entry.method === STORED ? data : entry.method === DEFLATED ? await inflate(data, maxBytes) : null
    if (content !== null && content.length === entry.uncompressedSize) images.set(path, content)
  }
  return images
}

function centralDirectory(bytes: Uint8Array): Map<string, Entry> | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  // The end record sits in the last 22 bytes, or up to 64KB further back when the zip carries a comment.
  const earliest = Math.max(0, bytes.length - 22 - 0xffff)
  let end = -1
  for (let offset = bytes.length - 22; offset >= earliest; offset--) {
    if (view.getUint32(offset, true) === END_OF_CENTRAL_DIRECTORY) {
      end = offset
      break
    }
  }
  if (end < 0) return null

  const count = view.getUint16(end + 10, true)
  let offset = view.getUint32(end + 16, true)
  const entries = new Map<string, Entry>()
  const decoder = new TextDecoder()
  for (let index = 0; index < count; index++) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== CENTRAL_DIRECTORY_ENTRY) return null
    const nameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength))
    entries.set(name, {
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      uncompressedSize: view.getUint32(offset + 24, true),
      localHeaderOffset: view.getUint32(offset + 42, true),
    })
    offset += 46 + nameLength + extraLength + commentLength
  }
  return entries
}

function entryData(bytes: Uint8Array, entry: Entry): Uint8Array | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const header = entry.localHeaderOffset
  if (header + 30 > bytes.length || view.getUint32(header, true) !== LOCAL_FILE_HEADER) return null
  // The local header's own name and extra lengths, which can differ from the central directory's.
  const start = header + 30 + view.getUint16(header + 26, true) + view.getUint16(header + 28, true)
  const finish = start + entry.compressedSize
  return finish > bytes.length ? null : bytes.subarray(start, finish)
}

/** Deflated data, or null once it would grow past `maxBytes` or does not inflate. */
async function inflate(data: Uint8Array, maxBytes: number): Promise<Uint8Array | null> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  const reader = stream.getReader()
  const chunks: Uint8Array[] = []
  let size = 0
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      size += value.length
      if (size > maxBytes) {
        await reader.cancel()
        return null
      }
      chunks.push(value)
    }
  } catch {
    return null
  }
  const out = new Uint8Array(size)
  let at = 0
  for (const chunk of chunks) {
    out.set(chunk, at)
    at += chunk.length
  }
  return out
}
